// =============================================================================
// CLIENT-SIDE DAILY ROW AGGREGATION
// =============================================================================
// PURPOSE: Aggregate raw setter_daily_performance rows for any date sub-range.
// This mirrors get_setter_performance_fast RPC logic but runs in the browser.
//
// WHY: The daily table stores SUM+COUNT pairs (not pre-computed averages) and
// raw value arrays (for medians). This was designed for exactly this use case:
// fetch the full month once, then slice any sub-range client-side.
//
// USAGE:
//   const metrics = aggregateDailyRows(dailyRows, "2026-03-01", "2026-03-07", nameMap);
//   // Instant — no network call, no loading state.
//
// WHAT THIS CANNOT DO (cross-day metrics, always need server):
// - followup_persistence (inter-day message dependency)
// - conversation_span (needs full conversation history across range boundary)
// These default to 0 and are filled by get_setter_cross_day_metrics RPC.
// =============================================================================

import type { SetterMetrics } from "../hooks/useSetterPerformance";

/** One row from setter_daily_performance table (fetched via .from().select()) */
export interface DailyRow {
  organization_id: string;
  setter_id: string;
  work_date: string; // YYYY-MM-DD
  // Volume
  total_conversations: number;
  real_conversations: number;
  total_messages_sent: number;
  total_words_sent: number;
  // Time metrics — SUM+COUNT pairs for correct cross-day averaging
  engaged_response_sum: number;
  engaged_response_count: number;
  engaged_response_values: number[] | null;
  session_gap_sum: number;
  session_gap_count: number;
  session_gap_values: number[] | null;
  session_count: number;
  total_work_minutes: number;
  lead_reply_sum: number;
  lead_reply_count: number;
  lead_reply_values: number[] | null;
  avg_reply_time_sum: number;
  avg_reply_time_count: number;
  avg_reply_time_values: number[] | null;
  // Sentiment
  positive_sentiment_count: number;
  negative_sentiment_count: number;
  neutral_sentiment_count: number;
  // Ignored
  total_ignored_conversations: number;
  total_ignore_events: number;
  // Work pattern (per-day values — AVG across days, not SUM)
  first_msg_minute_of_day: number | null;
  last_msg_minute_of_day: number | null;
  active_window_mins: number | null;
  work_mins: number | null;
  break_mins: number | null;
  // Reply turns (daily rows still have old bucket names — live RPC overrides in hook)
  total_reply_turns: number;
  reply_turn_ghost: number;
  reply_turn_outbound_no_reply: number;
  reply_turn_light: number;
  reply_turn_engaged: number;
  reply_turn_deep: number;
  // Calls
  calls_pitched: number;
  precalls_filled: number;
  precalls_total: number;
}

/** Compute median from an array of numbers (PERCENTILE_CONT(0.5) equivalent) */
function median(values: number[]): number | null {
  if (!values || values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function safeDiv(num: number, den: number): number | null {
  return den > 0 ? num / den : null;
}

function avgOfNonNull(values: (number | null)[]): number | null {
  const valid = values.filter((v): v is number => v !== null);
  if (valid.length === 0) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

/** Parse a value that might be a string (Postgres NUMERIC) to a number */
function num(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  return parseFloat(v as string) || 0;
}

/** Parse a Postgres NUMERIC[] that might come as string[] from PostgREST */
function numArray(v: unknown): number[] {
  if (!v || !Array.isArray(v)) return [];
  return v.map((x) => (typeof x === "number" ? x : parseFloat(x as string) || 0));
}

/**
 * Aggregate daily rows for a date range into SetterMetrics[].
 * Mirrors get_setter_performance_fast RPC but runs client-side.
 *
 * @param rows - All daily rows (can be broader than the range — we filter)
 * @param startDate - YYYY-MM-DD inclusive
 * @param endDate - YYYY-MM-DD inclusive
 * @param setterNames - Map of setter_id → display name
 */
export function aggregateDailyRows(
  rows: DailyRow[],
  startDate: string,
  endDate: string,
  setterNames: Record<string, string>
): SetterMetrics[] {
  // Filter to date range
  const filtered = rows.filter((r) => r.work_date >= startDate && r.work_date <= endDate);

  // Group by setter_id
  const bySetterId = new Map<string, DailyRow[]>();
  for (const row of filtered) {
    const existing = bySetterId.get(row.setter_id) || [];
    existing.push(row);
    bySetterId.set(row.setter_id, existing);
  }

  const result: SetterMetrics[] = [];

  for (const [setterId, setterRows] of bySetterId) {
    // ─── SUM-safe columns ──────────────────────────────────────
    const total_conversations = setterRows.reduce((s, r) => s + num(r.total_conversations), 0);
    const real_conversations = setterRows.reduce((s, r) => s + num(r.real_conversations), 0);
    const total_messages_sent = setterRows.reduce((s, r) => s + num(r.total_messages_sent), 0);
    const total_words_sent = setterRows.reduce((s, r) => s + num(r.total_words_sent), 0);
    const er_sum = setterRows.reduce((s, r) => s + num(r.engaged_response_sum), 0);
    const er_count = setterRows.reduce((s, r) => s + num(r.engaged_response_count), 0);
    const sg_sum = setterRows.reduce((s, r) => s + num(r.session_gap_sum), 0);
    const sg_count = setterRows.reduce((s, r) => s + num(r.session_gap_count), 0);
    const session_count = setterRows.reduce((s, r) => s + num(r.session_count), 0);
    const total_work_minutes = setterRows.reduce((s, r) => s + num(r.total_work_minutes), 0);
    const lr_sum = setterRows.reduce((s, r) => s + num(r.lead_reply_sum), 0);
    const lr_count = setterRows.reduce((s, r) => s + num(r.lead_reply_count), 0);
    const art_sum = setterRows.reduce((s, r) => s + num(r.avg_reply_time_sum), 0);
    const art_count = setterRows.reduce((s, r) => s + num(r.avg_reply_time_count), 0);
    const total_ignored_conversations = setterRows.reduce(
      (s, r) => s + num(r.total_ignored_conversations),
      0
    );
    const total_ignore_events = setterRows.reduce((s, r) => s + num(r.total_ignore_events), 0);
    const total_reply_turns = setterRows.reduce((s, r) => s + num(r.total_reply_turns), 0);
    // reply_turn_ghost/outbound_no_reply/light/engaged/deep are not aggregated here —
    // this function zeroes them in its output (see below) and the calling hook overrides
    // them via get_reply_turn_distribution RPC. NOTE: the raw DailyRow fields stay live;
    // proofBuckets.ts reads them straight off DailyRow[] — do not remove the interface fields.
    const calls_pitched = setterRows.reduce((s, r) => s + num(r.calls_pitched), 0);
    const precalls_filled = setterRows.reduce((s, r) => s + num(r.precalls_filled), 0);
    const precalls_total = setterRows.reduce((s, r) => s + num(r.precalls_total), 0);

    // ─── Medians — concat value arrays across days ─────────────
    const allEngagedValues = setterRows.flatMap((r) => numArray(r.engaged_response_values));
    const allSessionGapValues = setterRows.flatMap((r) => numArray(r.session_gap_values));
    const allLeadReplyValues = setterRows.flatMap((r) => numArray(r.lead_reply_values));
    const allAvgReplyValues = setterRows.flatMap((r) => numArray(r.avg_reply_time_values));

    // Longest gap: MAX of session_gap_values where < 300 min (5 hours)
    const gapValues = allSessionGapValues.filter((v) => v < 300);
    const longest_gap_mins = gapValues.length > 0 ? Math.max(...gapValues) : null;

    // ─── Work pattern — AVG across days (per-day values) ───────
    const work_days_count = setterRows.length;

    result.push({
      setter_id: setterId,
      setter_name: setterNames[setterId] || setterId,
      total_conversations,
      real_conversations,
      total_messages_sent,
      total_words_sent,
      // Averages from SUM/COUNT pairs
      engaged_response_avg_mins: safeDiv(er_sum, er_count),
      engaged_response_median_mins: median(allEngagedValues),
      engaged_response_count: er_count,
      session_gap_avg_mins: safeDiv(sg_sum, sg_count),
      session_gap_median_mins: median(allSessionGapValues),
      session_count,
      total_work_minutes,
      lead_reply_avg_mins: safeDiv(lr_sum, lr_count),
      lead_reply_median_mins: median(allLeadReplyValues),
      lead_reply_count: lr_count,
      avg_reply_time_avg_mins: safeDiv(art_sum, art_count),
      avg_reply_time_median_mins: median(allAvgReplyValues),
      avg_reply_time_count: art_count,
      total_ignored_conversations,
      total_ignore_events,
      avg_ignored_per_session: safeDiv(total_ignore_events, session_count),
      // Work pattern
      work_days_count,
      avg_first_msg_minute_of_day: avgOfNonNull(setterRows.map((r) => r.first_msg_minute_of_day)),
      avg_last_msg_minute_of_day: avgOfNonNull(setterRows.map((r) => r.last_msg_minute_of_day)),
      avg_active_window_mins: avgOfNonNull(setterRows.map((r) => r.active_window_mins)),
      avg_work_mins_per_day: avgOfNonNull(setterRows.map((r) => r.work_mins)),
      avg_break_mins_per_day: avgOfNonNull(setterRows.map((r) => r.break_mins)),
      longest_gap_mins,
      // Reply turns — zeroed here, overridden by live get_reply_turn_distribution RPC in hook
      avg_reply_turns: safeDiv(total_reply_turns, total_conversations),
      total_reply_turns,
      reply_turn_0: 0,
      reply_turn_1: 0,
      reply_turn_2: 0,
      reply_turn_3: 0,
      reply_turn_4: 0,
      reply_turn_5: 0,
      reply_turn_6: 0,
      reply_turn_7: 0,
      reply_turn_8: 0,
      reply_turn_9: 0,
      reply_turn_10: 0,
      reply_turn_11_12: 0,
      reply_turn_13_18: 0,
      reply_turn_19plus: 0,
      reply_turn_0_lead: 0,
      reply_turn_1_lead: 0,
      reply_turn_2_lead: 0,
      reply_turn_3_lead: 0,
      reply_turn_4_lead: 0,
      reply_turn_5_lead: 0,
      reply_turn_6_lead: 0,
      reply_turn_7_lead: 0,
      reply_turn_8_lead: 0,
      reply_turn_9_lead: 0,
      reply_turn_10_lead: 0,
      reply_turn_11_12_lead: 0,
      reply_turn_13_18_lead: 0,
      reply_turn_19plus_lead: 0,
      // Calls
      calls_pitched,
      precalls_filled,
      precalls_total,
      // Cross-day metrics — default to 0, filled by get_setter_cross_day_metrics RPC
      followup_persistence_1d: 0,
      followup_persistence_2d: 0,
      followup_persistence_3d: 0,
      followup_persistence_4d: 0,
      followup_persistence_5d: 0,
      followup_persistence_6d: 0,
      followup_persistence_7d: 0,
      followup_persistence_7plus: 0,
      convo_span_same_day: 0,
      convo_span_1_2d: 0,
      convo_span_3_6d: 0,
      convo_span_1_2w: 0,
      convo_span_2w_plus: 0,
      avg_convo_span_days: null,
      // S100 setter-performance tiles — from get_setter_cross_day_metrics, default 0
      leak_engaged_7d: 0,
      leak_untouched_7d: 0,
      depth_engaged_30d: 0,
      depth_deep_30d: 0,
      cliff_all_24_48h: 0,
      cliff_deep_24_48h: 0,
      actionable_all_lt24h: 0,
      actionable_deep_lt24h: 0,
      // Orphan/lost counts not in daily rows — merged by hook via separate RPCs
      orphan_pre_calls_count: 0,
      orphan_pre_calls_error: false,
      lost_pre_calls_count: 0,
      lost_pre_calls_error: false,
      // cross_day pending — set true by applyCrossDay when get_setter_cross_day_metrics resolves
      cross_day_hydrated: false,
    });
  }

  return result;
}
